import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Tabs,
  Tab,
} from '@mui/material';
import { apolloClient } from 'src/utils/api';
import { GET_SPLITS_AND_METRICS } from 'src/utils/gql/queries';

interface MultiTableSplitsAndMetricsViewerProps {
  connectionId: string;
  tables: Array<{ schema: string; tableName: string }>;
  onFieldsSelected: (splits: string[], metrics: string[]) => void;
  initialSplits?: string[];
  initialMetrics?: string[];
}

interface TableClassification {
  tableName: string;
  schema: string;
  splits: any[];
  metrics: any[];
  loading: boolean;
  error?: string;
}

export const MultiTableSplitsAndMetricsViewer: React.FC<
  MultiTableSplitsAndMetricsViewerProps
> = ({
  connectionId,
  tables,
  onFieldsSelected,
  initialSplits = [],
  initialMetrics = [],
}) => {
  const [tableClassifications, setTableClassifications] = useState<
    TableClassification[]
  >([]);
  const [selectedSplits, setSelectedSplits] = useState<string[]>(initialSplits);
  const [selectedMetrics, setSelectedMetrics] =
    useState<string[]>(initialMetrics);
  const [activeTab, setActiveTab] = useState(0);

  // Carica le classificazioni per tutte le tabelle
  useEffect(() => {
    const loadClassifications = async () => {
      const classifications: TableClassification[] = [];

      for (const table of tables) {
        classifications.push({
          tableName: table.tableName,
          schema: table.schema,
          splits: [],
          metrics: [],
          loading: true,
        });
      }

      setTableClassifications(classifications);

      // Carica i dati per ogni tabella usando Apollo Client
      for (let i = 0; i < tables.length; i++) {
        const table = tables[i];
        try {
          // Usa Apollo Client per la query
          const { data } = await apolloClient.query({
            query: GET_SPLITS_AND_METRICS,
            variables: {
              connectionId,
              schema: table.schema,
              table: table.tableName,
            },
          });

          if (data) {
            setTableClassifications((prev) =>
              prev.map((classification, index) =>
                index === i
                  ? {
                      ...classification,
                      splits: data.getSplitsAndMetrics.splits,
                      metrics: data.getSplitsAndMetrics.metrics,
                      loading: false,
                    }
                  : classification,
              ),
            );
          }
        } catch (error) {
          console.error('Errore nel caricamento della classificazione:', error);
          setTableClassifications((prev) =>
            prev.map((classification, index) =>
              index === i
                ? {
                    ...classification,
                    loading: false,
                    error:
                      error instanceof Error
                        ? error.message
                        : 'Errore sconosciuto',
                  }
                : classification,
            ),
          );
        }
      }
    };

    if (tables.length > 0) {
      loadClassifications();
    }
  }, [connectionId, tables]);

  // Mantieni selezioni preesistenti provenienti dal config
  useEffect(() => {
    setSelectedSplits(initialSplits);
  }, [initialSplits]);
  useEffect(() => {
    setSelectedMetrics(initialMetrics);
  }, [initialMetrics]);

  const allSplits = tableClassifications.flatMap((tc) =>
    tc.splits.map((split) => `${tc.tableName}.${split.fieldName}`),
  );
  const allMetrics = tableClassifications.flatMap((tc) =>
    tc.metrics.map((metric) => `${tc.tableName}.${metric.fieldName}`),
  );

  const handleSplitToggle = (tableName: string, fieldName: string) => {
    const fullFieldName = `${tableName}.${fieldName}`;
    setSelectedSplits((prev) =>
      prev.includes(fullFieldName)
        ? prev.filter((f) => f !== fullFieldName)
        : [...prev, fullFieldName],
    );
  };

  const handleMetricToggle = (tableName: string, fieldName: string) => {
    const fullFieldName = `${tableName}.${fieldName}`;
    setSelectedMetrics((prev) =>
      prev.includes(fullFieldName)
        ? prev.filter((f) => f !== fullFieldName)
        : [...prev, fullFieldName],
    );
  };

  const handleSelectAllSplits = () => {
    setSelectedSplits(allSplits);
  };

  const handleSelectAllMetrics = () => {
    setSelectedMetrics(allMetrics);
  };

  const handleDeselectAllSplits = () => {
    setSelectedSplits([]);
  };

  const handleDeselectAllMetrics = () => {
    setSelectedMetrics([]);
  };

  const handleContinue = () => {
    const metricKeys = new Set(
      tableClassifications.flatMap((tc) =>
        tc.metrics.map((metric) => `${tc.tableName}.${metric.fieldName}`),
      ),
    );

    const normalizedMetrics = new Set(selectedMetrics);
    const normalizedSplits: string[] = [];

    for (const fieldKey of selectedSplits) {
      if (metricKeys.has(fieldKey)) {
        normalizedMetrics.add(fieldKey);
      } else {
        normalizedSplits.push(fieldKey);
      }
    }

    onFieldsSelected(normalizedSplits, Array.from(normalizedMetrics));
  };

  const scrollablePanelSx = {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    minWidth: 0,
    height: { xs: 360, md: 420 },
    maxHeight: { xs: '55vh', md: '60vh' },
    overflow: 'hidden',
  } as const;

  const renderFieldTable = (
    fields: any[],
    tableName: string,
    type: 'split' | 'metric',
    selectedFields: string[],
    onToggle: (tableName: string, fieldName: string) => void,
  ) => (
    <TableContainer
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'auto',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold' }}>Campo</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Motivo</TableCell>
            <TableCell sx={{ fontWeight: 'bold', width: 120 }}>
              Seleziona
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {fields.map((field: any, index: number) => {
            const fullFieldName = `${tableName}.${field.fieldName}`;
            const isSelected = selectedFields.includes(fullFieldName);
            const chipColor = type === 'split' ? 'primary' : 'secondary';
            const buttonColor = type === 'split' ? 'primary' : 'secondary';

            return (
              <TableRow key={`${fullFieldName}-${index}`}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {field.fieldName}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={field.dataType}
                    size="small"
                    color={chipColor}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {field.reason}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Button
                    size="small"
                    variant={isSelected ? 'contained' : 'outlined'}
                    color={buttonColor}
                    onClick={() => onToggle(tableName, field.fieldName)}
                  >
                    {isSelected ? '✓ Selezionato' : 'Seleziona'}
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const isLoading = tableClassifications.some((tc) => tc.loading);
  const hasError = tableClassifications.some((tc) => tc.error);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (hasError) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Errore nel caricamento della classificazione per alcune tabelle
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        🎯 Classificazione Automatica: Splits e Metrics
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        gutterBottom
        sx={{ mb: 3 }}
      >
        Tabelle selezionate: <strong>{tables.length}</strong> • Totale:{' '}
        {allSplits.length} splits, {allMetrics.length} metrics
      </Typography>

      {/* Tabs per le tabelle */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
        >
          {tableClassifications.map((tc, index) => (
            <Tab
              key={index}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">{tc.tableName}</Typography>
                  <Chip
                    label={`${tc.splits.length + tc.metrics.length}`}
                    size="small"
                    color="primary"
                  />
                </Box>
              }
            />
          ))}
        </Tabs>
      </Box>

      {/* Contenuto del tab attivo */}
      {tableClassifications[activeTab] && (
        <Box>
          <Typography variant="h6" gutterBottom>
            📊 {tableClassifications[activeTab].tableName}
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
              gap: 2,
              mb: 3,
              alignItems: 'stretch',
            }}
          >
            <Paper variant="outlined" sx={{ p: 2, ...scrollablePanelSx }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  mb: 2,
                  flexShrink: 0,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={`${tableClassifications[activeTab].splits.length}`}
                    color="primary"
                    size="small"
                  />
                  <Typography variant="h6">Splits (Dati Categorici)</Typography>
                </Box>
                <Box>
                  <Button
                    onClick={handleSelectAllSplits}
                    sx={{ mr: 1 }}
                    size="small"
                  >
                    Seleziona Tutti
                  </Button>
                  <Button onClick={handleDeselectAllSplits} size="small">
                    Deseleziona Tutti
                  </Button>
                </Box>
              </Box>

              {renderFieldTable(
                tableClassifications[activeTab].splits,
                tableClassifications[activeTab].tableName,
                'split',
                selectedSplits,
                handleSplitToggle,
              )}
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, ...scrollablePanelSx }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  mb: 2,
                  flexShrink: 0,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={`${tableClassifications[activeTab].metrics.length}`}
                    color="secondary"
                    size="small"
                  />
                  <Typography variant="h6">Metrics (Dati Numerici)</Typography>
                </Box>
                <Box>
                  <Button
                    onClick={handleSelectAllMetrics}
                    sx={{ mr: 1 }}
                    size="small"
                  >
                    Seleziona Tutti
                  </Button>
                  <Button onClick={handleDeselectAllMetrics} size="small">
                    Deseleziona Tutti
                  </Button>
                </Box>
              </Box>

              {renderFieldTable(
                tableClassifications[activeTab].metrics,
                tableClassifications[activeTab].tableName,
                'metric',
                selectedMetrics,
                handleMetricToggle,
              )}
            </Paper>
          </Box>
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 3,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Selezionati: {selectedSplits.length} splits, {selectedMetrics.length}{' '}
          metrics
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleContinue}
          disabled={selectedSplits.length === 0 && selectedMetrics.length === 0}
        >
          Continua con {selectedSplits.length + selectedMetrics.length} campi
        </Button>
      </Box>
    </Box>
  );
};
